import { useCallback, useState, useMemo } from "react";
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Box,
  InlineGrid,
  EmptyState,
  Banner,
  SortableList,
  SortableResourceItem,
} from "@medentic/polaris-react";
import {
  PlusIcon,
  DeleteIcon,
  EditIcon,
  ExternalIcon,
  PlusCircleIcon,
} from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { Instructor } from "@features/events/types";
import { SocialProfile } from "@features/events/types";
import AddSocialProfileModal from "./AddSocialProfileModal";
import useEventMetadata from "@features/events/hooks/useEventMetadata";
import emptySocialImage from "@shared/assets/empty-states/empty-social.svg";

interface SocialMediaProfilesCardProps {
  instructorData: Partial<Instructor>;
  onDataChange: (updates: Partial<Instructor>) => void;
  fieldErrors: Record<string, string | undefined>;
}

interface SocialProfileItemProps {
  profile: SocialProfile;
  onEdit: (profile: SocialProfile) => void;
  onDelete: (profile: SocialProfile) => void;
  getSocialPlatformLabel: (value: string) => string;
}

const SocialProfileItem: React.FC<SocialProfileItemProps> = ({
  profile,
  onEdit,
  onDelete,
  getSocialPlatformLabel,
}) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <SortableResourceItem
      accessibilityLabel={t("instructorDetails.socialProfiles.editProfile")}
      dragHandleAccessibilityLabel={`Reorder ${getSocialPlatformLabel(profile.social)}`}
    >
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <InlineGrid columns={["twoThirds", "oneThird"]}>
          <InlineStack gap="200" align="start" blockAlign="center" wrap={false}>
            <BlockStack gap="100">
              <Text as="span" variant="bodyMd">
                {getSocialPlatformLabel(profile.social)}
              </Text>
            </BlockStack>
          </InlineStack>

          <InlineStack gap="200" align="space-between" blockAlign="center">
            <Box></Box>
            <Box opacity={isHovered ? "1" : "0"} paddingInlineEnd="100">
              <InlineStack gap="200" blockAlign="center" align="end">
                <Button
                  icon={ExternalIcon}
                  variant="tertiary"
                  onClick={() => window.open(profile.url, "_blank")}
                  accessibilityLabel={t(
                    "instructorDetails.socialProfiles.openProfile"
                  )}
                />
                <Button
                  icon={EditIcon}
                  variant="tertiary"
                  onClick={() => onEdit(profile)}
                  accessibilityLabel={t(
                    "instructorDetails.socialProfiles.editProfile"
                  )}
                />
                <Button
                  icon={DeleteIcon}
                  tone="critical"
                  variant="tertiary"
                  onClick={() => onDelete(profile)}
                  accessibilityLabel={t(
                    "instructorDetails.socialProfiles.deleteProfile"
                  )}
                />
              </InlineStack>
            </Box>
          </InlineStack>
        </InlineGrid>
      </div>
    </SortableResourceItem>
  );
};

export default function SocialMediaProfilesCard({
  instructorData,
  onDataChange,
  fieldErrors,
}: SocialMediaProfilesCardProps) {
  const { t } = useTranslation();
  const { socialProfileOptions } = useEventMetadata();
  const [showModal, setShowModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState<SocialProfile | null>(
    null
  );

  const socialProfiles = useMemo(
    () => instructorData.social || [],
    [instructorData.social]
  );

  // Get social media related errors
  const socialErrors = useMemo(() => {
    const errors: string[] = [];
    Object.keys(fieldErrors).forEach((key) => {
      if (key.startsWith("social.")) {
        const error = fieldErrors[key];
        if (error) {
          errors.push(error);
        }
      }
    });
    return errors;
  }, [fieldErrors]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingProfile(null);
  }, []);

  const handleSaveProfile = useCallback(
    (profile: SocialProfile) => {
      if (editingProfile) {
        // Update existing profile
        const index = socialProfiles.findIndex((p) => p === editingProfile);
        if (index !== -1) {
          const updatedProfiles = [...socialProfiles];
          updatedProfiles[index] = profile;
          onDataChange({ social: updatedProfiles });
        }
      } else {
        // Add new profile
        const updatedProfiles = [...socialProfiles, profile];
        onDataChange({ social: updatedProfiles });
      }
      handleCloseModal();
    },
    [socialProfiles, onDataChange, editingProfile, handleCloseModal]
  );

  const handleDeleteProfile = useCallback(
    (profile: SocialProfile) => {
      const updatedProfiles = socialProfiles.filter((p) => p !== profile);
      onDataChange({ social: updatedProfiles });
    },
    [socialProfiles, onDataChange]
  );

  const handleSocialProfilesReorder = useCallback(
    (reorderedProfiles: SocialProfile[]) => {
      onDataChange({ social: reorderedProfiles });
    },
    [onDataChange]
  );

  const getSocialPlatformLabel = useCallback(
    (value: string) => {
      return (
        socialProfileOptions.find((platform) => platform.value === value)?.label ||
        value
    );
  }, 
    [socialProfileOptions]
  );

  const handleEditProfile = useCallback((profile: SocialProfile) => {
    setEditingProfile(profile);
    setShowModal(true);
  }, []);

  const handleAddProfile = useCallback(() => {
    setEditingProfile(null);
    setShowModal(true);
  }, []);

  const renderSocialProfile = useCallback(
    (profile: SocialProfile) => {
      return (
        <SocialProfileItem
          profile={profile}
          onEdit={handleEditProfile}
          onDelete={handleDeleteProfile}
          getSocialPlatformLabel={getSocialPlatformLabel}
        />
      );
    },
    [handleEditProfile, handleDeleteProfile, getSocialPlatformLabel]
  );

  return (
    <>
      <AddSocialProfileModal
        open={showModal}
        onClose={handleCloseModal}
        onSave={handleSaveProfile}
        editingProfile={editingProfile}
      />

      <Card>
        <BlockStack gap="400">
          <Text variant="headingMd" as="h2">
            {t("instructorDetails.socialProfiles.title")}
          </Text>

          <Text as="p" variant="bodyMd" tone="subdued">
            {t("instructorDetails.socialProfiles.description")}
          </Text>

          {socialErrors.length > 0 && (
            <Banner tone="critical">
              <BlockStack gap="100">
                {socialErrors.map((error, index) => (
                  <Text key={index} as="p" variant="bodyMd">
                    {error}
                  </Text>
                ))}
              </BlockStack>
            </Banner>
          )}

          {socialProfiles.length === 0 ? (
            <EmptyState
              heading={t("instructorDetails.socialProfiles.emptyState")}
              action={{
                content: t("instructorDetails.socialProfiles.addButton"),
                icon: PlusIcon,
                onAction: handleAddProfile,
              }}
              image={emptySocialImage}
            />
          ) : (
            <Box
              borderWidth="025"
              borderColor="border"
              borderRadius="200"
              background="bg-surface"
            >
              <SortableList
                items={socialProfiles}
                onChange={handleSocialProfilesReorder}
                idForItem={(profile) => `${profile.social}-${profile.url}`}
                renderItem={(item) => renderSocialProfile(item)}
                showDividers={true}
              />
            </Box>
          )}

          {socialProfiles.length > 0 && (
            <Box>
              <Button
                variant="plain"
                icon={PlusCircleIcon}
                onClick={handleAddProfile}
              >
                {t("instructorDetails.socialProfiles.addButton")}
              </Button>
            </Box>
          )}
        </BlockStack>
      </Card>
    </>
  );
}
